import React, { useState, useEffect } from 'react';
import { X, Wallet, Settings } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const CreateAdModal = ({ onClose, onSuccess, tokens = [], onOpenPaymentSettings }) => {
    const [formData, setFormData] = useState({
        type: 'buy',
        asset: tokens.length > 0 ? tokens[0].symbol : 'USDT',
        fiatCurrency: 'INR',
        price: '',
        totalAmount: '',
        minLimit: '',
        maxLimit: '',
        paymentMethods: [],
        terms: ''
    });

    const [loading, setLoading] = useState(false);
    const [p2pBalances, setP2pBalances] = useState([]);
    const [selectedBalance, setSelectedBalance] = useState(0);
    const [userPaymentMethods, setUserPaymentMethods] = useState([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPaymentToAdd, setSelectedPaymentToAdd] = useState(null);

    const paymentMethodOptions = ['UPI', 'Bank Transfer', 'IMPS', 'Google Pay', 'PhonePe'];

    // Fetch P2P balances and payment methods on mount  
    useEffect(() => {
        fetchP2PBalances();
        fetchUserPaymentMethods();
    }, []);

    // Update asset when tokens load
    useEffect(() => {
        if (tokens.length > 0 && !formData.asset) {
            setFormData(prev => ({ ...prev, asset: tokens[0].symbol }));
        }
    }, [tokens]);

    // Update selected balance when asset changes
    useEffect(() => {
        const balance = p2pBalances.find(b => b.symbol === formData.asset);
        setSelectedBalance(balance ? parseFloat(balance.balance) - parseFloat(balance.lockedBalance || 0) : 0);
    }, [formData.asset, p2pBalances]);

    const fetchP2PBalances = async () => {
        try {
            const res = await api.get('/p2p/balance');
            setP2pBalances(res.data.balances || []);
        } catch (error) {
            console.error('Failed to fetch P2P balances:', error);
        }
    };

    const fetchUserPaymentMethods = async () => {
        try {
            const res = await api.get('/p2p/payment-methods');
            setUserPaymentMethods(res.data.paymentMethods || []);
        } catch (error) {
            console.error('Failed to fetch payment methods:', error);
        }
    };

    const isPaymentMethodConfigured = (method) => {
        return userPaymentMethods.some(pm => pm.method === method && pm.active);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (formData.paymentMethods.length === 0) {
            toast.error('Please select at least one payment method');
            return;
        }

        // Check if all selected payment methods are configured
        const unconfigured = formData.paymentMethods.filter(m => !isPaymentMethodConfigured(m));
        if (unconfigured.length > 0) {
            toast.error(`Please configure payment details for: ${unconfigured.join(', ')}`);
            return;
        }

        // Check balance for sell ads
        if (formData.type === 'sell') {
            const amount = parseFloat(formData.totalAmount);
            if (amount > selectedBalance) {
                toast.error(`Insufficient P2P balance. Available: ${selectedBalance.toFixed(6)} ${formData.asset}`);
                return;
            }
        }

        setLoading(true);
        try {
            await api.post('/p2p/ads', formData);
            toast.success('Ad posted successfully');
            onSuccess();
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to post ad');
        } finally {
            setLoading(false);
        }
    };

    const togglePaymentMethod = (method) => {
        setFormData(prev => {
            const methods = prev.paymentMethods.includes(method)
                ? prev.paymentMethods.filter(m => m !== method)
                : [...prev.paymentMethods, method];
            return { ...prev, paymentMethods: methods };
        });
    };

    const handleSavePaymentMethod = async (details) => {
        try {
            await api.post('/p2p/payment-methods', {
                method: selectedPaymentToAdd,
                details,
                active: true
            });
            toast.success(`${selectedPaymentToAdd} details saved!`);
            await fetchUserPaymentMethods();
            // Auto-select the newly configured method
            setFormData(prev => ({
                ...prev,
                paymentMethods: [...prev.paymentMethods, selectedPaymentToAdd]
            }));
            setShowPaymentModal(false);
            setSelectedPaymentToAdd(null);
        } catch (error) {
            toast.error('Failed to save payment method');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-[#181a20] rounded-3xl border border-gray-800 p-8 max-w-2xl w-full shadow-2xl scale-100 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto custom-scrollbar">
                <div className="flex justify-between items-center mb-8">
                    <h3 className="text-2xl font-bold text-white">Post New Ad</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors bg-gray-800/50 p-2 rounded-full hover:bg-gray-800">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">I want to</label>
                            <div className="flex bg-[#0b0e11] p-1 rounded-xl">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, type: 'buy' })}
                                    className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all ${formData.type === 'buy'
                                        ? 'bg-green-500 text-white shadow-lg'
                                        : 'text-gray-400 hover:text-white'
                                        }`}
                                >
                                    Buy
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, type: 'sell' })}
                                    className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all ${formData.type === 'sell'
                                        ? 'bg-red-500 text-white shadow-lg'
                                        : 'text-gray-400 hover:text-white'
                                        }`}
                                >
                                    Sell
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Asset</label>
                            <select
                                value={formData.asset}
                                onChange={(e) => setFormData({ ...formData, asset: e.target.value })}
                                className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                            >
                                {tokens.length > 0 ? tokens.map(token => (
                                    <option key={token.id} value={token.symbol}>{token.symbol} - {token.name}</option>
                                )) : (
                                    <option value="USDT">USDT</option>
                                )}
                            </select>
                            {formData.type === 'sell' && (
                                <div className="flex items-center gap-2 mt-2 p-2 bg-[#0b0e11] rounded-lg border border-gray-800">
                                    <Wallet size={14} className="text-blue-400" />
                                    <span className="text-xs text-gray-400">P2P Balance:</span>
                                    <span className={`text-xs font-bold ${selectedBalance > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {selectedBalance.toFixed(6)} {formData.asset}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Price ({formData.fiatCurrency})</label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.price}
                                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-blue-500 transition-colors"
                                placeholder="0.00"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Total Amount ({formData.asset})</label>
                            <input
                                type="number"
                                step="0.000001"
                                value={formData.totalAmount}
                                onChange={(e) => setFormData({ ...formData, totalAmount: e.target.value })}
                                className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-blue-500 transition-colors"
                                placeholder="0.00"
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Min Limit ({formData.fiatCurrency})</label>
                            <input
                                type="number"
                                value={formData.minLimit}
                                onChange={(e) => setFormData({ ...formData, minLimit: e.target.value })}
                                className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-blue-500 transition-colors"
                                placeholder="Min"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Max Limit ({formData.fiatCurrency})</label>
                            <input
                                type="number"
                                value={formData.maxLimit}
                                onChange={(e) => setFormData({ ...formData, maxLimit: e.target.value })}
                                className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-blue-500 transition-colors"
                                placeholder="Max"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Payment Methods</label>
                        <p className="text-xs text-gray-500 mb-3">Configure your payment methods in the "Payment Methods" tab first</p>
                        <div className="flex flex-wrap gap-2">
                            {paymentMethodOptions.map(method => {
                                const isConfigured = isPaymentMethodConfigured(method);
                                const isSelected = formData.paymentMethods.includes(method);
                                return (
                                    <button
                                        key={method}
                                        type="button"
                                        onClick={() => isConfigured && togglePaymentMethod(method)}
                                        disabled={!isConfigured}
                                        title={!isConfigured ? 'Add details in Payment Methods tab' : ''}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all flex items-center gap-2 ${!isConfigured
                                            ? 'bg-gray-900 border-gray-800 text-gray-600 cursor-not-allowed opacity-50'
                                            : isSelected
                                                ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                                                : 'bg-[#0b0e11] border-green-700 text-green-400 hover:border-green-500'
                                            }`}
                                    >
                                        {isConfigured ? '✓' : '○'} {method}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Terms & Conditions</label>
                        <textarea
                            value={formData.terms}
                            onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                            className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-blue-500 transition-colors min-h-[100px]"
                            placeholder="Enter your terms (e.g., 'No third-party payments')"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/20 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Posting...' : 'Post Ad'}
                    </button>
                </form>
            </div>

            {/* Payment Method Details Modal */}
            {showPaymentModal && (
                <PaymentDetailsModal
                    method={selectedPaymentToAdd}
                    onClose={() => { setShowPaymentModal(false); setSelectedPaymentToAdd(null); }}
                    onSave={handleSavePaymentMethod}
                />
            )}
        </div>
    );
};

// Inline Payment Details Modal Component
const PaymentDetailsModal = ({ method, onClose, onSave }) => {
    const [details, setDetails] = useState({});

    const getFieldsForMethod = (method) => {
        switch (method) {
            case 'UPI':
                return [{ key: 'upiId', label: 'UPI ID', placeholder: 'yourname@upi' }];
            case 'Bank Transfer':
            case 'IMPS':
                return [
                    { key: 'accountName', label: 'Account Holder Name', placeholder: 'John Doe' },
                    { key: 'accountNumber', label: 'Account Number', placeholder: '1234567890' },
                    { key: 'ifscCode', label: 'IFSC Code', placeholder: 'SBIN0001234' },
                    { key: 'bankName', label: 'Bank Name', placeholder: 'State Bank of India' }
                ];
            case 'Google Pay':
            case 'PhonePe':
                return [{ key: 'mobileNumber', label: 'Mobile Number', placeholder: '9876543210' }];
            default:
                return [{ key: 'details', label: 'Details', placeholder: 'Enter payment details' }];
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(details);
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
            <div className="bg-[#1e2026] rounded-2xl border border-gray-700 p-6 max-w-md w-full">
                <h4 className="text-lg font-bold text-white mb-4">Add {method} Details</h4>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {getFieldsForMethod(method).map(field => (
                        <div key={field.key}>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">{field.label}</label>
                            <input
                                type="text"
                                value={details[field.key] || ''}
                                onChange={(e) => setDetails({ ...details, [field.key]: e.target.value })}
                                className="w-full bg-[#0b0e11] border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500"
                                placeholder={field.placeholder}
                                required
                            />
                        </div>
                    ))}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-600"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500"
                        >
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateAdModal;
